import type { StateCreator } from 'zustand';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { logError } from 'setup/logger/logError';
import { issueClient } from 'shared/domain/issue/issueClient';
import type { IssueModel } from 'shared/domain/issue/issueModel';
import { subscribeToEntityChanges } from 'store/entityCollection/subscribeToEntityChanges';
import { createLatestRead } from 'store/latestRead';

/** Issues of the opened inspection, archived ones included. */
export type IssuesOfInspectionSlice = {
  inspectionIssues: IssueModel[];
  inspectionIssuesLoading: boolean;
  /** Reads the inspection's issues and reads them again after issue changes; returns the close (resets). */
  openInspectionIssues: (inspectionId: string, timezone: string) => () => void;
  /** An issue just created from the inspection, shown before the next read brings it. */
  addInspectionIssue: (issue: IssueModel) => void;
  resetInspectionIssues: () => void;
};

const initialState: Pick<IssuesOfInspectionSlice, 'inspectionIssues' | 'inspectionIssuesLoading'> = {
  inspectionIssues: [],
  inspectionIssuesLoading: false,
};

export const createIssuesOfInspectionSlice: StateCreator<IssuesOfInspectionSlice> = (set, get) => {
  const latestRead = createLatestRead();

  const resetInspectionIssues = (): void => {
    latestRead.cancel();
    set(initialState);
  };

  return {
    ...initialState,
    openInspectionIssues: (inspectionId, timezone) => {
      const readIssues = (): void => {
        void latestRead.run(() => issueClient.getOfInspection(inspectionId, timezone), {
          onResult: (inspectionIssues) => set({ inspectionIssues, inspectionIssuesLoading: false }),
          onError: (error) => {
            logError('Error while reading the issues of the inspection', error);
            set({ inspectionIssuesLoading: false });
          },
        });
      };
      set({ inspectionIssuesLoading: true });
      readIssues();
      const stopFollowingChanges = subscribeToEntityChanges(issueClient, readIssues);
      return (): void => {
        stopFollowingChanges();
        resetInspectionIssues();
      };
    },
    addInspectionIssue: (issue) => {
      const current = get().inspectionIssues;
      if (!current.some((item) => areObjectIdsEqual(item._id, issue._id))) {
        set({ inspectionIssues: [...current, issue] });
      }
    },
    resetInspectionIssues,
  };
};
